import {
  useCallback,
  useEffect,
  useMemo,
  FC,
  memo,
  useState,
  startTransition,
} from 'react';
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { skipToken } from '@reduxjs/toolkit/query';
import {
  skipViewingInvestorTagForAccountsQuery,
  useGetAccountsQuery,
} from '@/store/accountsApi';
import { useAuthIsLoaded } from '@/hooks/useAuthIsLoaded';

/** AccountType 8 = Managed by Advisor (legacy convention) */
const MANAGED_BY_ADVISOR_ACCOUNT_TYPE = 8;

export interface InstitutionRow {
  institutionId: number;
  name: string;
  isManaged: boolean;
}

export interface InstitutionsTabProps {
  initialExcludedInstitutions?: number[];
  setFieldValue: (field: string, value: number[]) => void;
  /** When provided, "Clear all" clears every tab's exclusion filters. */
  onClearAllFilters?: () => void;
}

const InstitutionsTab: FC<InstitutionsTabProps> = memo(
  ({ initialExcludedInstitutions = [], setFieldValue, onClearAllFilters }) => {
    const { t } = useTranslation();
    const authIsLoaded = useAuthIsLoaded();
    const [isInitialized, setIsInitialized] = useState(false);
    const [excludedInstitutions, setExcludedInstitutions] = useState<number[]>(
      initialExcludedInstitutions,
    );

    useEffect(() => {
      setExcludedInstitutions(initialExcludedInstitutions);
    }, [initialExcludedInstitutions]);

    const { data: accountsData, isLoading: accountsLoading } =
      useGetAccountsQuery(
        authIsLoaded
          ? { showClosed: false, ...skipViewingInvestorTagForAccountsQuery }
          : skipToken,
      );

    useEffect(() => {
      if (
        !isInitialized &&
        accountsData?.Records &&
        accountsData.Records.length > 0 &&
        !accountsLoading
      ) {
        setIsInitialized(true);
      }
    }, [isInitialized, accountsData, accountsLoading]);

    const institutions = useMemo<InstitutionRow[]>(() => {
      if (!accountsData?.Records || accountsData.Records.length === 0) {
        return [];
      }
      const byId = new Map<
        number,
        { name: string; hasManagedAccount: boolean }
      >();
      accountsData.Records.forEach(
        (account: {
          InstitutionId: number;
          InstitutionName: any;
          AccountType: number;
        }) => {
          if (!account.InstitutionId || !account.InstitutionName) return;
          const existing = byId.get(account.InstitutionId);
          const isManaged =
            account.AccountType === MANAGED_BY_ADVISOR_ACCOUNT_TYPE;
          if (!existing) {
            byId.set(account.InstitutionId, {
              name: account.InstitutionName,
              hasManagedAccount: isManaged,
            });
          } else if (isManaged) {
            existing.hasManagedAccount = true;
          }
        },
      );
      return Array.from(byId.entries())
        .map(([institutionId, { name, hasManagedAccount }]) => ({
          institutionId,
          name,
          isManaged: hasManagedAccount,
        }))
        .sort((a, b) => a.name.localeCompare(b.name));
    }, [accountsData]);

    const handleInstitutionChange = useCallback(
      (institutionId: number) => {
        setExcludedInstitutions((prev) => {
          const isExcluded = prev.includes(institutionId);
          const next = isExcluded
            ? prev.filter((id) => id !== institutionId)
            : [...prev, institutionId];
          startTransition(() => {
            setFieldValue('excludedInstitutions', next);
          });
          return next;
        });
      },
      [setFieldValue],
    );

    const handleSelectAll = useCallback(
      (checked: boolean) => {
        const next = checked ? [] : institutions.map((i) => i.institutionId);
        setExcludedInstitutions(next);
        startTransition(() => {
          setFieldValue('excludedInstitutions', next);
        });
      },
      [institutions, setFieldValue],
    );

    const allIncluded = excludedInstitutions.length === 0;
    const someIncluded =
      excludedInstitutions.length > 0 &&
      excludedInstitutions.length < institutions.length;

    if (!isInitialized) {
      if (!accountsData?.Records || accountsData.Records.length === 0) {
        return (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        );
      }
      return null;
    }

    if (institutions.length === 0) {
      return (
        <Paper sx={{ p: 2, maxHeight: '400px', overflow: 'auto' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Typography
              variant='h6'
              color='warning.main'
            >
              ⚠
            </Typography>
            <Typography
              variant='body1'
              sx={{ ml: 1, color: 'warning.main' }}
            >
              {t('noInstitutionsFound')}
            </Typography>
          </Box>
          <Typography
            variant='body2'
            color='text.secondary'
          >
            {t('noInstitutionsFoundDescription')}
          </Typography>
        </Paper>
      );
    }

    return (
      <Box
        sx={{
          p: 2,
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          minHeight: 0,
        }}
      >
        <Box
          sx={{
            flex: 1,
            overflow: 'auto',
            minHeight: 0,
          }}
        >
          <Table
            size='small'
            stickyHeader
          >
            <TableHead>
              <TableRow>
                <TableCell
                  sx={{
                    borderBottom: 1,
                    borderColor: 'divider',
                    py: 1.5,
                    backgroundColor: 'background.paper',
                  }}
                >
                  <Typography
                    variant='body2'
                    sx={{ fontWeight: 500 }}
                  >
                    {t('name')}
                  </Typography>
                </TableCell>
                <TableCell
                  sx={{
                    borderBottom: 1,
                    borderColor: 'divider',
                    py: 1.5,
                    backgroundColor: 'background.paper',
                    textAlign: 'center',
                  }}
                >
                  <Typography
                    variant='body2'
                    sx={{ fontWeight: 500 }}
                  >
                    {t('filters.managed')}
                  </Typography>
                </TableCell>
                <TableCell
                  padding='checkbox'
                  sx={{
                    borderBottom: 1,
                    borderColor: 'divider',
                    py: 1.5,
                    backgroundColor: 'background.paper',
                  }}
                >
                  <Checkbox
                    size='small'
                    checked={allIncluded}
                    indeterminate={someIncluded}
                    onChange={(_, checked) => handleSelectAll(checked)}
                    aria-label={t('selectAll')}
                  />
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {institutions.map((row) => {
                const isExcluded = excludedInstitutions.includes(
                  row.institutionId,
                );
                return (
                  <TableRow
                    key={row.institutionId}
                    sx={{ '&:last-child td': { borderBottom: 0 } }}
                  >
                    <TableCell sx={{ py: 1.5 }}>
                      <Typography variant='body2'>{row.name}</Typography>
                    </TableCell>
                    <TableCell sx={{ py: 1.5, textAlign: 'center' }}>
                      {row.isManaged ? (
                        <Typography
                          variant='body2'
                          component='span'
                          sx={{ color: 'text.secondary' }}
                          aria-hidden
                        >
                          ✓
                        </Typography>
                      ) : null}
                    </TableCell>
                    <TableCell
                      padding='checkbox'
                      sx={{ py: 1.5 }}
                    >
                      <Checkbox
                        size='small'
                        checked={!isExcluded}
                        onChange={() =>
                          handleInstitutionChange(row.institutionId)
                        }
                        aria-label={`${row.name} – ${t('filters.includeInView')}`}
                      />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Box>
        <Box sx={{ mt: 2, textAlign: 'right', flexShrink: 0 }}>
          <Button
            size='small'
            onClick={() => {
              if (onClearAllFilters) {
                onClearAllFilters();
              } else {
                setExcludedInstitutions([]);
                startTransition(() => {
                  setFieldValue('excludedInstitutions', []);
                });
              }
            }}
          >
            {t('resetFilters')}
          </Button>
        </Box>
      </Box>
    );
  },
  (prevProps, nextProps) => {
    return (
      prevProps.setFieldValue === nextProps.setFieldValue &&
      prevProps.onClearAllFilters === nextProps.onClearAllFilters &&
      prevProps.initialExcludedInstitutions ===
        nextProps.initialExcludedInstitutions
    );
  },
);
InstitutionsTab.displayName = 'InstitutionsTab';

export default InstitutionsTab;
